import axios, { isAxiosError } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigationType, useSearchParams } from 'react-router-dom';

import { CategoryAdsFilter } from './components/CategoryAdsFilter/CategoryAdsFilter';
import { AdsPageHeader } from './components/AdsPageHeader';
import ProductService from '../../services/ProductService';
import BtnPageContainer from './components/BtnPageContainer';
import Card from '../../components/Card/Card';
import CardSkeleton from '../../components/Card/CardSkeleton';
import Search from '../../components/search/Search';
import Slider from './components/Slider/Slider';
import { storeCategory } from '../../store/categorySlice';
import { useDebounce } from '../../hooks/useDebounce';
import { useDispatch, useSelector } from 'react-redux';
import { setFilterObjectState, ITEMS_PER_PAGE, resetIsSubmit } from '../../store/filtersSlice';
import useScrollRestoration from '../../hooks/useScrollRestoration';

const CategoryProductPage = () => {
  let [searchParams] = useSearchParams();
  const dispatch = useDispatch();

  const [pageAds, setPageAds] = useState({ totalAds: 0, numOfPages: 0, currentPage: 1, ads: [] });
  const [currPage, setCurrPage] = useState(parseInt(searchParams.get('page')) || 1);
  const { pathname, state } = useLocation();
  const [searchText, setSearchText] = useState(searchParams.get('q') || '');
  const search = useDebounce(searchText, 500);
  const [sortOption, setSortOption] = useState('newest'); // newest, oldest
  const [allCategories, setAllCategories] = useState([]);
  const [allSubCategories, setAllSubCategories] = useState([]);
  const [allSubCategoriesFields, setAllSubCategoriesFields] = useState([]);
  const [sliders, setSliders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const location = useLocation();
  const filterObject = useSelector((store) => store.filtersStore);

  // Detektovanje mobilnog view-a
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640); // Mobilni je ispod 640px
  const currentItemsPerPage = isMobile ? 10 : ITEMS_PER_PAGE;

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const setFilterObject = useCallback(
    (filters) => {
      dispatch(setFilterObjectState(filters));
    },
    [dispatch]
  );

  const getSliders = async () => {
    let res = await ProductService.getAllSliders();
    setSliders(res.data);
  };

  useEffect(() => {
    getSliders();
    setCurrPage(parseInt(searchParams.get('page') || 1));
  }, [location, searchParams]);

  const navigationType = useNavigationType();

  const getAllSubcategoriesFields = async (id) => {
    try {
      const response = await ProductService.getSubCategoriesFields(id);
      const subCategoriesFields = response.data;
      setAllSubCategoriesFields(subCategoriesFields);
    } catch (error) {
      console.log(error);
    }
  };

  const getFilterAds = useCallback(
    async (source) => {
      // subCategory
      const { askForPrice, priceFrom, priceUpTo, fields, search, isSubmit, subCategories, ...filterObjectRest } = filterObject;

      if (!isSubmit) {
        return;
      }

      // Build query parameters, appending multiple subCategory params when multiselect is active
      const params = new URLSearchParams();
      // Base (rest of filterObject)
      Object.entries({ ...filterObjectRest }).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') params.set(k, v);
      });
      // Specials
      params.set('fields', JSON.stringify(fields));
      if (search) params.set('search', search);
      if (priceFrom) params.set('priceFrom', priceFrom);
      if (priceUpTo) params.set('priceUpTo', priceUpTo);
      if (askForPrice) params.set('askForPrice', '1');
      params.set('sort', sortOption === 'priceLow' || sortOption === 'priceHigh' ? 'priceFrom' : 'createAt');
      params.set('order', String(sortOption === 'oldest' || sortOption === 'priceLow' ? 1 : -1));
      params.set('limit', String(currentItemsPerPage));
      params.set('page', String(parseInt(currPage) || 1));
      if (Array.isArray(subCategories) && subCategories.length > 0) {
        // Backend currently expects a single subCategory with regex; use OR pattern
        const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const orPattern = subCategories.map((s) => escapeRegex(s)).join('|');
        params.set('subCategory', orPattern);
      }
      const fullPath = `products?${params.toString()}`;

      try {
        setIsLoading(true);
        // Do not clear the list if we already have ads (prevents scroll jumps)
        setPageAds((prev) => ({ ...prev, ads: prev.ads.length > 0 ? prev.ads : [] }));
        const response = await ProductService.getAds(fullPath, { cancelToken: source.token });
        const newPageAds = {
          totalAds: response.data.totalCount,
          numOfPages: Math.ceil(response.data.totalCount / currentItemsPerPage),
          currentPage: currPage || 1,
          ads: response.data.data,
        };
        setPageAds(newPageAds);
        setIsLoading(false);
        dispatch(resetIsSubmit());
      } catch (error) {
        if (isAxiosError(error)) return;
        console.log(error);
        setIsLoading(false);
        dispatch(resetIsSubmit());
      }
    },
    [filterObject, currPage, currentItemsPerPage, dispatch, sortOption]
  );

  useEffect(() => {
    let source = axios.CancelToken.source();
    getFilterAds(source);
    return () => {
      source.cancel();
    };
  }, [getFilterAds]);

  useEffect(() => {
    if (filterObject.search === searchText) return;
    setFilterObject({ ...filterObject, isSubmit: true, search: searchText });
  }, [search, filterObject, searchText, setFilterObject]);

  useEffect(() => {
    if (filterObject.page === currPage) return;
    setFilterObject({ ...filterObject, isSubmit: true, page: currPage });
  }, [currPage, filterObject, setFilterObject]);

  useEffect(() => {
    setFilterObject({ ...filterObject, isSubmit: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortOption]);

  useEffect(() => {
    if (state?.category) dispatch(storeCategory(state.category));
    if (state?.search) setSearchText(state?.search);
    const element = document.getElementById('filters');
    if (element && state?.search && navigationType !== 'POP') {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }, [state?.search, state?.category, navigationType, dispatch]);

  // const CardSkeletonRender = () => {
  //   return [...Array(currentItemsPerPage)].map((_el, index) => <CardSkeleton key={index + '-' + currPage}></CardSkeleton>);
  // };

  const onCurrPageChange = (page) => {
    setCurrPage(page);
    let elem = document.getElementById('search');
    if (elem && navigationType !== 'POP') elem.scrollIntoView({ behavior: 'smooth' });
  };

  useScrollRestoration(!isLoading && pageAds.ads.length > 0, pathname);

  return (
    <div>
      <AdsPageHeader
        allCategories={allCategories}
        setAllCategories={setAllCategories}
        allSubCategories={allSubCategories}
        setAllSubCategories={setAllSubCategories}
        setAllSubCategoriesFields={setAllSubCategoriesFields}
        getAllSubcategoriesFields={getAllSubcategoriesFields}
        setSearchText={setSearchText}
      />
      <div id='filters' className='flex w-full flex-col items-center px-4 pb-6 lg:flex-row lg:items-start lg:justify-center lg:px-11'>
        <div className='flex w-full max-w-[88rem] flex-col lg:flex-row lg:gap-8'>
          {/* Sidebar Filter - Vidljiv samo na desktopu (lg+) */}
          <div className='hidden lg:block lg:w-[21rem]'>
            <CategoryAdsFilter itemsPerPage={currentItemsPerPage} currPage={currPage} allSubCategoriesFields={allSubCategoriesFields} />
          </div>

          <div className='w-full'>
            <Search
              setSearchText={setSearchText}
              numberOfAds={pageAds.totalAds}
              className='block pb-6 sm:hidden'
              searchText={searchText}
              sortOption={sortOption}
              setSortOption={setSortOption}
            />

            <div className='w-full' id='search'>
              <Search
                setSearchText={setSearchText}
                numberOfAds={pageAds.totalAds}
                className='hidden pb-6 sm:block'
                searchText={searchText}
                sortOption={sortOption}
                setSortOption={setSortOption}
              />

              {/* Filter - Vidljiv na mobilnom i tabletu, sakriven na desktopu (jer je tamo u sidebar-u) */}
              <div className='lg:hidden'>
                <CategoryAdsFilter itemsPerPage={currentItemsPerPage} currPage={currPage} allSubCategoriesFields={allSubCategoriesFields} />
              </div>

              {/* CARDS - Desktop layout sa banerima */}
              <div className='mt-4'>
                {/* Mobilna verzija - 5 oglasa -> baner -> baner -> 5 oglasa */}
                <div className='sm:hidden'>
                  <div className='grid w-full grid-cols-1 place-items-center gap-[1rem]'>
                    {isLoading
                      ? [...Array(5)].map((_el, index) => <CardSkeleton key={index + '-' + currPage}></CardSkeleton>)
                      : pageAds.ads.slice(0, 5).map((ad) => {
                          return <Card ad={ad} key={ad._id + '-' + currPage}></Card>;
                        })}
                  </div>

                  {/* Baneri na mobilnom nakon 5 oglasa */}
                  {!isLoading && pageAds.ads.length > 5 && (
                    <div className='my-4 flex flex-col gap-4'>
                      <Slider
                        className='h-[150px] w-full rounded-xl bg-transparent'
                        banners={sliders['slider1']?.map((slider) => import.meta.env.VITE_IMAGE_URL + slider.img.substring(1))}
                        link={sliders['slider1']?.map((slider) => slider.link)}
                      />
                      <Slider
                        className='h-[150px] w-full rounded-xl bg-transparent'
                        banners={sliders['slider2']?.map((slider) => import.meta.env.VITE_IMAGE_URL + slider.img.substring(1))}
                        link={sliders['slider2']?.map((slider) => slider.link)}
                      />
                    </div>
                  )}

                  <div className='mt-4 grid w-full grid-cols-1 place-items-center gap-[1rem]'>
                    {isLoading
                      ? [...Array(currentItemsPerPage - 5)].map((_el, index) => (
                          <CardSkeleton key={index + 5 + '-' + currPage}></CardSkeleton>
                        ))
                      : pageAds.ads.slice(5, 10).map((ad) => {
                          return <Card ad={ad} key={ad._id + '-' + currPage}></Card>;
                        })}
                  </div>
                </div>

                {/* Tablet (sm/md) verzija - 10 oglasa -> baneri -> 10 oglasa (3 kolone) */}
                <div className='hidden sm:block lg:hidden'>
                  <div className='grid grid-cols-3 place-items-center gap-[1rem]'>
                    {isLoading
                      ? [...Array(10)].map((_el, index) => <CardSkeleton key={index + '-' + currPage}></CardSkeleton>)
                      : pageAds.ads.slice(0, 10).map((ad) => {
                          return <Card ad={ad} key={ad._id + '-' + currPage}></Card>;
                        })}
                  </div>

                  {/* Dva banera horizontalno */}
                  {pageAds.ads.length > 10 && (
                    <div className='my-8 grid w-full grid-cols-2 gap-6'>
                      <Slider
                        className='h-[200px] w-full rounded-xl bg-transparent'
                        banners={sliders['slider1']?.map((slider) => import.meta.env.VITE_IMAGE_URL + slider.img.substring(1))}
                        link={sliders['slider1']?.map((slider) => slider.link)}
                      />
                      <Slider
                        className='h-[200px] w-full rounded-xl bg-transparent'
                        banners={sliders['slider2']?.map((slider) => import.meta.env.VITE_IMAGE_URL + slider.img.substring(1))}
                        link={sliders['slider2']?.map((slider) => slider.link)}
                      />
                    </div>
                  )}

                  {/* Sledećih 10 oglasa */}
                  {pageAds.ads.length > 10 && (
                    <div className='grid grid-cols-3 place-items-center gap-[1rem]'>
                      {isLoading
                        ? [...Array(10)].map((_el, index) => <CardSkeleton key={index + 10 + '-' + currPage}></CardSkeleton>)
                        : pageAds.ads.slice(10, 20).map((ad) => {
                            return <Card ad={ad} key={ad._id + '-' + currPage}></Card>;
                          })}
                    </div>
                  )}
                </div>

                {/* Desktop (lg+) verzija - 8 oglasa -> baneri -> preostali (4 kolone) */}
                <div className='hidden lg:block'>
                  <div className='grid grid-cols-4 place-items-center gap-[1rem]'>
                    {isLoading
                      ? [...Array(8)].map((_el, index) => <CardSkeleton key={index + '-' + currPage}></CardSkeleton>)
                      : pageAds.ads.slice(0, 8).map((ad) => {
                          return <Card ad={ad} key={ad._id + '-' + currPage}></Card>;
                        })}
                  </div>

                  {/* Dva banera horizontalno */}
                  {pageAds.ads.length > 8 && (
                    <div className='my-8 grid w-full grid-cols-2 gap-6'>
                      <Slider
                        className='h-[200px] w-full rounded-xl bg-transparent'
                        banners={sliders['slider1']?.map((slider) => import.meta.env.VITE_IMAGE_URL + slider.img.substring(1))}
                        link={sliders['slider1']?.map((slider) => slider.link)}
                      />
                      <Slider
                        className='h-[200px] w-full rounded-xl bg-transparent'
                        banners={sliders['slider2']?.map((slider) => import.meta.env.VITE_IMAGE_URL + slider.img.substring(1))}
                        link={sliders['slider2']?.map((slider) => slider.link)}
                      />
                    </div>
                  )}

                  {/* Sledećih 8 oglasa (ukupno 16 na stranici na desktopu) */}
                  {pageAds.ads.length > 8 && (
                    <div className='grid grid-cols-4 place-items-center gap-[1rem]'>
                      {isLoading
                        ? [...Array(8)].map((_el, index) => <CardSkeleton key={index + 8 + '-' + currPage}></CardSkeleton>)
                        : pageAds.ads.slice(8, 16).map((ad) => {
                            return <Card ad={ad} key={ad._id + '-' + currPage}></Card>;
                          })}
                    </div>
                  )}
                </div>
              </div>

              {/* Baneri ispod paginacije - samo desktop */}
              <div className='mt-8 hidden w-full grid-cols-2 gap-6 sm:grid'>
                <Slider
                  className='h-[200px] w-full rounded-xl bg-transparent'
                  banners={sliders['slider4']?.map((slider) => import.meta.env.VITE_IMAGE_URL + slider.img.substring(1))}
                  link={sliders['slider4']?.map((slider) => slider.link)}
                />
                <Slider
                  className='h-[200px] w-full rounded-xl bg-transparent'
                  banners={sliders['slider5']?.map((slider) => import.meta.env.VITE_IMAGE_URL + slider.img.substring(1))}
                  link={sliders['slider5']?.map((slider) => slider.link)}
                />
              </div>
              {/* Mobilni banneri ispod paginacije */}
              <div className='mt-[2.875rem] h-full w-full place-items-end gap-4 rounded-xl sm:hidden'>
                <div className='mx-auto flex w-full flex-col items-center justify-center gap-6'>
                  <Slider
                    className='h-[150px] w-full rounded-xl bg-transparent'
                    banners={sliders['slider4']?.map((slider) => import.meta.env.VITE_IMAGE_URL + slider.img.substring(1))}
                    link={sliders['slider4']?.map((slider) => slider.link)}
                  />
                  <Slider
                    className='h-[150px] w-full rounded-xl bg-transparent'
                    banners={sliders['slider5']?.map((slider) => import.meta.env.VITE_IMAGE_URL + slider.img.substring(1))}
                    link={sliders['slider5']?.map((slider) => slider.link)}
                  />
                </div>
              </div>
              {/* Pagination */}
              <div className='flex w-full justify-center py-4 pt-12 md:pl-6'>
                {pageAds.numOfPages > 1 && <BtnPageContainer pageAds={pageAds} setPageAds={setPageAds} setCurrPage={onCurrPageChange} />}
              </div>
            </div>
          </div>
        </div>

        {/* Stari banneri sa desne strane - potpuno sakriveni */}
        <div className='hidden'>{/* ... svi stari Slider elementi ... */}</div>
      </div>
    </div>
  );
};

export default CategoryProductPage;
